import * as React from 'react';
import { useEffect, useState } from 'react';
import { flow } from 'fp-ts/lib/function';
import {
    Box,
    Divider,
    Drawer,
    Slider,
    Typography,
    WithStyles as IWithStyles,
    withStyles,
    IconButton,
} from '@material-ui/core';
import {
    IToolsDrawerState,
    IToolSize,
    IToolPerspective,
} from './toolsSettings';
import KeyframeLogoIcon from '../../../../Assets/keyframe_logo.png';
import { styles } from './TelestrationControlsStyles';
import { EditMode } from './../../Types';
import {
    handleConnectToolPerspectiveToTelestrationManager,
    handleConnectToolSizeToTelestrationManager,
} from './helpers';
import { ITelestrationStateMgr } from 'src/Features/TelestrationsVideoLibrary/Types';
import {
    toggleArrowType,
    withTelestrationState,
    setModeAction,
} from 'src/Features/TelestrationsVideoLibrary/State';
import { ColorPicker } from '../ColorPicker';
import RewindLeftIcon from '../../../../Assets/rewind_left.png';
import { CloseIcon } from 'src/Assets/svgs/CloseIcon';
interface IProps {
    toolsDrawerState: IToolsDrawerState;
    toolSizes: IToolSize;
    telestrationStateMgr: ITelestrationStateMgr;
    setToolSizes: React.Dispatch<React.SetStateAction<IToolSize>>;
    toolTypes: any;
    setToolTypes: any;
    toolPerspectives: IToolPerspective;
    setToolPerspectives: React.Dispatch<React.SetStateAction<IToolPerspective>>;
    setToolsDrawerState: React.Dispatch<
        React.SetStateAction<IToolsDrawerState>
    >;
    onColorPick: ({ rgb }: any) => void;
    needColorPicker: any;
}

const toolTypeArray = ['arrow', 'freearrow', 'straightarrow', 'playercutout'];

const initialToolTypeState = {
    straight: false,
    dashed: false,
};

const toolAdjustmentDrawer = ({
    classes,
    telestrationStateMgr,
    toolsDrawerState,
    toolSizes,
    setToolSizes,
    toolTypes,
    setToolTypes,
    toolPerspectives,
    setToolPerspectives,
    setToolsDrawerState,
    onColorPick,
    needColorPicker,
}: IProps & IWithStyles) => {
    const [isToolTypeShow, setIsToolTypeShow] = useState(false);
    const [, setToolTypeActive] = useState(initialToolTypeState);
    const {
        state: { editMode, telestrationManager },
        dispatchAction,
    } = telestrationStateMgr;
    const LOCAL_STORAGE_TOOLS_PERSPECTIVES_KEY = 'TOOLS_PERSPECTIVES_KEY';
    const LOCAL_STORAGE_TOOLS_SIZES_KEY = 'TOOLS_SIZES_KEY';
    const LOCAL_TOOLS_TYPE_KEY = 'TOOLS_TYPE_KEY';

    useEffect(() => {
        document.addEventListener(
            'mousewheel',
            handleAddToolsAdjustmentListeners,
            {
                passive: false,
            }
        );

        return () =>
            document.removeEventListener(
                'mousewheel',
                handleAddToolsAdjustmentListeners
            );
    }, []);

    useEffect(() => {
        if (
            toolsDrawerState[editMode] &&
            editMode !== 'default' &&
            editMode !== 'chromakey' &&
            !toolsDrawerState[editMode]?.wasClosed
        ) {
            setToolsDrawerState((prev: any) => ({
                ...prev,
                [editMode]: {
                    wasClosed: false,
                    currentlyOpen: true,
                },
            }));
        } else if (editMode === 'default' || editMode === 'chromakey') {
            setToolsDrawerState((prev: any) => ({
                ...prev,
                [editMode]: {
                    wasClosed: true,
                    currentlyOpen: false,
                },
            }));
        }
    }, [editMode]);

    useEffect(() => {
        if (toolTypeArray.includes(editMode)) {
            setIsToolTypeShow(true);
        } else {
            setIsToolTypeShow(false);
        }
    }, [editMode]);

    useEffect(() => {
        handleArrowTypeSwitching();
    }, [editMode]);

    const handleArrowTypeSwitching = () => {
        let arrowType;
        switch (editMode) {
            case 'arrow':
            case 'freearrow':
            case 'straightarrow':
            case 'playercutout':
                arrowType = toolTypes[editMode].straight ? 0 : 1;
                if (arrowType !== telestrationManager.creationObject.type) {
                    dispatchAction(
                        toggleArrowType({ editMode, value: arrowType })
                    );
                }
                break;
            default:
                return;
        }
    };

    function handleAddToolsAdjustmentListeners(event: WheelEvent) {
        event.preventDefault();
        handleConnectToolSizeToTelestrationManager(
            telestrationManager,
            handleChangeToolSize
        );
        handleConnectToolPerspectiveToTelestrationManager(
            telestrationManager,
            handleChangeToolPerspective
        );
    }

    function handleChangeToolSize(newValue: number, currentEditMode: EditMode) {
        const prevValue = toolSizes[currentEditMode].currentValue;
        // for debounce effect
        if (Math.abs(prevValue - newValue) >= 2) {
            const currentToolSetting = {
                ...toolSizes[currentEditMode],
                currentValue: newValue,
            };
            setToolSizes((prev) => {
                const newState = {
                    ...prev,
                    [currentEditMode]: currentToolSetting,
                };
                localStorage.setItem(
                    LOCAL_STORAGE_TOOLS_SIZES_KEY,
                    JSON.stringify(newState)
                );
                return newState;
            });
        }
    }

    function handleChangeToolPerspective(
        newValue: number,
        currentEditMode: string
    ) {
        const prevValue = toolPerspectives[currentEditMode].currentValue;
        // for debounce effect
        if (Math.abs(prevValue - newValue) > 0.01) {
            const currentToolSetting = {
                ...toolPerspectives[currentEditMode],
                currentValue: newValue,
            };
            setToolPerspectives((prev) => {
                const newState = {
                    ...prev,
                    [currentEditMode]: currentToolSetting,
                };
                localStorage.setItem(
                    LOCAL_STORAGE_TOOLS_PERSPECTIVES_KEY,
                    JSON.stringify(newState)
                );
                return newState;
            });
        }
    }

    function handleSwitchArrow(action: 'straight' | 'dashed') {
        const actionTM = action === 'straight' ? 0 : 1;
        dispatchAction(toggleArrowType({ editMode, value: actionTM }));

        setToolTypeActive({
            straight: 'straight' === action,
            dashed: 'dashed' === action,
        });
        setToolTypes((prev: any) => {
            const newState = {
                ...prev,
                [editMode]: {
                    straight: 'straight' === action,
                    dashed: 'dashed' === action,
                },
            };
            localStorage.setItem(
                LOCAL_TOOLS_TYPE_KEY,
                JSON.stringify(newState)
            );
            return newState;
        });
    }

    console.log(editMode);

    const activeToolType =
        editMode !== 'arrow' &&
        editMode !== 'freearrow' &&
        editMode !== 'straightarrow' &&
        editMode !== 'playercutout'
            ? toolTypes.arrow
            : toolTypes[editMode];

    const open =
        toolsDrawerState[editMode] && toolsDrawerState[editMode].currentlyOpen;

    const openSizes =
        toolSizes[editMode].maxValue && toolSizes[editMode].maxValue;
    const openPers =
        toolPerspectives[editMode].maxValue &&
        toolPerspectives[editMode].maxValue;

    const drawerRef = React.useRef<HTMLDivElement>(null);

    // Close drawer when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                drawerRef.current &&
                !drawerRef.current.contains(event.target as Node)
            ) {
                setToolsDrawerState((prev: any) => ({
                    ...prev,
                    [editMode]: {
                        wasClosed: true,
                        currentlyOpen: false,
                    },
                }));
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [drawerRef, editMode, setToolsDrawerState]);

    return (
        <Box
            ref={drawerRef}
            style={
                open
                    ? {
                          width: '280px',
                          userSelect: 'none',
                          position: 'fixed',
                          backgroundColor: '#121217',
                          borderRadius: '20px',
                          left: 70,
                          top: 200,
                          zIndex: 5000,
                          border: 'solid 1px rgba(255, 255, 255, 0.12)',
                          padding: '18px 24px 0 18px',
                          gap: 0,
                          display: 'flex',
                          flexDirection: 'column',
                      }
                    : { display: 'none' }
            }
        >
            <>
                <div
                    style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: '-5px',
                        right: '-5px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#db2c29',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                    }}
                    onClick={() => {
                        setToolsDrawerState((prev: any) => ({
                            ...prev,
                            [editMode]: {
                                wasClosed: true,
                                currentlyOpen: false,
                            },
                        }));
                    }}
                >
                    <CloseIcon />
                </div>
                <ColorPicker onPick={onColorPick} open={needColorPicker} />
                <Box style={{ marginBottom: 12 }}>
                    <Box>
                        <Typography
                            style={
                                openSizes
                                    ? {
                                          fontSize: 15,
                                          fontWeight: 500,
                                          lineHeight: '24px',
                                      }
                                    : { display: 'none' }
                            }
                            id='continuous-slider'
                        >
                            Size
                        </Typography>
                        <Slider
                            style={openSizes ? {} : { display: 'none' }}
                            max={toolSizes[editMode].maxValue || 0}
                            min={toolSizes[editMode].minValue || 0}
                            step={2}
                            value={toolSizes[editMode].currentValue || 0}
                            aria-labelledby='continuous-slider'
                            onChange={(
                                e: React.ChangeEvent<{}>,
                                newValue: number
                            ) => handleChangeToolSize(newValue, editMode)}
                            classes={{
                                root: classes.slider,
                                thumb: classes.thumb,
                                track: classes.track,
                                rail: classes.rail,
                            }}
                        />
                    </Box>
                    {/* input for access current tool size from TelestrationManager AccessSliderInput function */}
                    <input
                        value={toolSizes[editMode].currentValue || 0}
                        type='hidden'
                        id='size-slider'
                    />
                    <Box style={{ marginTop: 4 }}>
                        <Typography
                            style={
                                openSizes
                                    ? {
                                          fontSize: 15,
                                          fontWeight: 500,
                                          lineHeight: '24px',
                                      }
                                    : { display: 'none' }
                            }
                            id='slider'
                        >
                            Perspective
                        </Typography>
                        <Slider
                            style={openSizes ? {} : { display: 'none' }}
                            max={toolPerspectives[editMode].maxValue || 0}
                            min={toolPerspectives[editMode].minValue || 0}
                            step={0.09}
                            value={toolPerspectives[editMode].currentValue || 0}
                            aria-labelledby='slider'
                            onChange={(
                                e: React.ChangeEvent<{}>,
                                newValue: number
                            ) =>
                                handleChangeToolPerspective(newValue, editMode)
                            }
                            classes={{
                                root: classes.slider,
                                thumb: classes.thumb,
                                track: classes.track,
                                rail: classes.rail,
                            }}
                        />
                    </Box>
                    {/* input for access current tool size from TelestrationManager AccessSliderInput function */}
                    <input
                        value={toolPerspectives[editMode].currentValue || 0}
                        type='hidden'
                        id='perspective-slider'
                    />
                </Box>
                {isToolTypeShow && (
                    <Box style={{ marginBottom: 12 }}>
                        <Typography
                            style={{
                                fontSize: 15,
                                fontWeight: 500,
                                lineHeight: '24px',
                            }}
                        >
                            Line Type
                        </Typography>
                        <Box className={classes.toolTypeContainer}>
                            <IconButton
                                aria-label={'straight'}
                                className={`${classes.iconButtonDrawer}
                                        ${
                                            activeToolType.straight &&
                                            classes.iconButtonDrawerActive
                                        }`}
                                onClick={() => handleSwitchArrow('straight')}
                            >
                                <Box className={classes.toolTypeLine} />
                            </IconButton>
                            <IconButton
                                aria-label={'dashed'}
                                className={`${classes.iconButtonDrawer}
                                    ${
                                        activeToolType.dashed &&
                                        classes.iconButtonDrawerActive
                                    }`}
                                onClick={() => handleSwitchArrow('dashed')}
                            >
                                <Box
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        gap: '6px',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        className={classes.toolTypeDashCommon}
                                    />
                                    <Box className={classes.toolTypeDash} />
                                    <Box
                                        className={classes.toolTypeDashCommon}
                                    />
                                </Box>
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </>
        </Box>
    );
};

export const ToolAdjustmentDrawer = flow(
    withTelestrationState,
    withStyles(styles)
)(toolAdjustmentDrawer);
